import './underwriting.quote.bind.modal.scss';
import './underwriting.quote.create.button.scss';

import * as Yup from 'yup';

import { Box, Modal, Typography } from '@mui/material';
import {
  CONVERGE_UUID,
  UnderwritingSubmissionDetailPageSlug,
} from '@converge/data';
import {
  FormContainer,
  FormFieldContainer,
  FormNumberInputAdornmentField,
  NewConvergeButton,
} from '../../../presentation';
import { FormProvider, useForm } from 'react-hook-form';
import {
  RaterMode,
  SubmissionType,
  useCreateDefaultQuoteMutation,
} from '@converge/graphql';
import { capitalizeFirstLetter, toNumber } from '@converge/utils';
import { useEffect, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { FormSearchableSelectField } from '../../../presentation/components';
import { useGetEntities } from '@converge/graphql';
import { convergeLogger } from '@converge/config';
import { useConvergeSnackBar } from '../../../contexts/use-snackbar';
import { useTranslation } from 'react-i18next';
import { useUnderwritingSubmissionDetailPage } from '../underwriting.submission.detail.hooks';
import { useUnderwritingSubmissionStateContext } from '../underwriting.submission.context';
import { yupResolver } from '@hookform/resolvers/yup';
import { modalCommonStyle } from '../../entity/utils';

export const UnderwritingQuoteCreateButton = ({
  show = false,
  diffrentLabel = false,
}) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const {
    navigate,
    params: { submissionUuid },
  } = useUnderwritingSubmissionDetailPage();
  const { notify } = useConvergeSnackBar();
  const { createDefaultQuote, loading, error } =
    useCreateDefaultQuoteMutation();

  useEffect(() => {
    if (error) {
      convergeLogger.error(error);
      notify({
        message: `${t(
          'underwriting.quote.header.createOption.error',
          'Failed to create quote option'
        )}: ${error.message ?? 'unknown error'}`,
        variant: 'error',
      });
    }
  }, [error, notify, t]);

  return (
    <>
      {show && (
        <Box>
          <NewConvergeButton
            loading={loading}
            label={t(
              diffrentLabel
                ? 'Create additional quote option'
                : 'Create quote option'
            )}
            onClick={async () => {
              setOpen(true);
            }}
            startIcon={''}
          />
        </Box>
      )}
      <CreateDefaultQuoteDialog
        loading={loading}
        isOpen={open}
        onClose={() => {
          setOpen(false);
        }}
        disabled={!show}
        onSubmit={async ({
          type,
          convergePrimaryAggregateLimit,
          convergePrimaryPolicyRetention,
          insurerPrimaryLimit,
          insurerPrimaryPremium,
          insurerPrimaryRetention,
          insurerPrimaryParticipantUuid,
        }) => {
          const { data, errors } = await createDefaultQuote({
            variables: {
              submissionUuid,
              insurerPrimaryLimit:
                type === SubmissionType.Excess
                  ? toNumber(insurerPrimaryLimit)
                  : undefined,
              insurerPrimaryRetention:
                type === SubmissionType.Excess
                  ? toNumber(insurerPrimaryRetention)
                  : undefined,
              insurerPrimaryPremium:
                type === SubmissionType.Excess
                  ? toNumber(insurerPrimaryPremium)
                  : undefined,
              insurerPrimaryParticipantUuid:
                type === SubmissionType.Excess
                  ? insurerPrimaryParticipantUuid
                  : undefined,
              convergePrimaryAggregateLimit:
                type === SubmissionType.Excess
                  ? toNumber(insurerPrimaryLimit) ?? -1
                  : toNumber(convergePrimaryAggregateLimit) ?? -1,
              convergePrimaryPolicyRetention:
                type === SubmissionType.Excess
                  ? toNumber(insurerPrimaryRetention) ?? -1
                  : toNumber(convergePrimaryPolicyRetention) ?? -1,
            },
          });
          if (errors && errors.length > 0) {
            convergeLogger.error(errors);
            throw new Error(
              `Failed to create quote: ${errors[0]?.message ?? ''}`
            );
          }
          if (!data?.createDefaultQuote?.uuid) {
            throw new Error(
              'Failed to create quote (no ID returned from API).'
            );
          }
          setOpen(false);
          navigate.page({
            sectionUrl: `${UnderwritingSubmissionDetailPageSlug.Quote}/${
              RaterMode.EditQuote
            }/${data?.createDefaultQuote?.uuid ?? ''}`,
          });
        }}
      />
    </>
  );
};

const PrimaryInsurerSelectField = () => {
  const { entities = [], loading } = useGetEntities({
    isInsurer: true,
    pageSize: 200,
  });

  const filteredEntities = entities.filter(
    (entity) => entity.uuid !== CONVERGE_UUID
  );

  return (
    <FormSearchableSelectField
      name="insurerPrimaryParticipantUuid"
      label="PRIMARY INSURANCE"
      required
      placeholder="Select the insurer that wrote the primary policy"
      containerWidth={12}
      isLoading={loading}
      options={filteredEntities.sort((a, b) =>
        (a.name || '').localeCompare(b.name || '')
      )}
      mapToOption={(entity) => ({
        value: entity.uuid,
        label: entity.name ?? 'Insurer',
      })}
      noOptionsText="No insurers available"
      disableClearable={false}
      clearOnBlur={false}
    />
  );
};

interface ICreateDefaultQuoteDialogFormValues {
  type?: SubmissionType | null;
  insurerPrimaryLimit?: null | number;
  insurerPrimaryRetention?: null | number;
  insurerPrimaryPremium?: null | number;
  insurerPrimaryParticipantUuid?: string | null;
  convergePrimaryAggregateLimit: number;
  convergePrimaryPolicyRetention: number;
}
const submissionTypeValues = SubmissionType
  ? Object.values(SubmissionType)
  : [];

const transformEmptyToNumber = (
  value: string,
  originalValue?: string | null
) => {
  // Handle empty string and convert to number
  if (
    originalValue === '' ||
    originalValue === null ||
    originalValue === undefined
  ) {
    return undefined;
  }
  return Number(originalValue);
};
const excessNumberField = (label: string) =>
  Yup.number()
    .transform(transformEmptyToNumber)
    .typeError(`${label} must be a valid number`)
    .min(0.01, `${label} must be greater than 0`)
    .when('type', {
      is: 'EXCESS',
      then: (schema) => schema.required(`${label} is required`),
      otherwise: (schema) => schema.nullable().notRequired(),
    });

const CREATE_DEFAULT_QUOTE_SCHEMA = Yup.object().shape({
  type: Yup.string()
    .oneOf(submissionTypeValues as string[])
    .required(),

  insurerPrimaryLimit: excessNumberField('Primary policy limit').integer(
    'Please enter a valid dollar amount'
  ),
  insurerPrimaryRetention: excessNumberField('Primary policy retention'),
  insurerPrimaryPremium: excessNumberField('Primary policy premium'),

  insurerPrimaryParticipantUuid: Yup.string().when('type', {
    is: 'EXCESS',
    then: (schema) => schema.required('Primary insurer selection is required'),
    otherwise: (schema) => schema.nullable().notRequired(),
  }),

  convergePrimaryAggregateLimit:
    excessNumberField('Limit').required('Limit is required'),
  convergePrimaryPolicyRetention: excessNumberField('Retention').required(
    'Retention is required'
  ),
});

interface ICreateDefaultQuoteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: ICreateDefaultQuoteDialogFormValues) => void;
  loading?: boolean;
  disabled?: boolean;
}

export const CreateDefaultQuoteDialog = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
  disabled,
}: ICreateDefaultQuoteDialogProps) => {
  const {
    info: {
      insuredEntityName,
      policyTypeName,
      isExcessSubmission,
      submissionType,
    },
  } = useUnderwritingSubmissionStateContext();

  const formMethods = useForm<ICreateDefaultQuoteDialogFormValues>({
    defaultValues: {
      type: submissionType,
      convergePrimaryAggregateLimit: 1000 * 1000,
      convergePrimaryPolicyRetention: 2.5 * 1000,
      insurerPrimaryLimit: 1000 * 1000,
      insurerPrimaryPremium: 10 * 1000,
      insurerPrimaryRetention: 2.5 * 1000,
      insurerPrimaryParticipantUuid: null,
    },
    resolver: yupResolver(CREATE_DEFAULT_QUOTE_SCHEMA),
  });

  return (
    <Modal
      open={isOpen}
      onClose={() => onClose()}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <Box
        sx={modalCommonStyle({ height: 480, width: 600 })}
        className="create-quote-option-modal"
      >
        <FormProvider {...formMethods}>
          <IconButton
            aria-label="close"
            onClick={() => onClose()}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          <form onSubmit={formMethods.handleSubmit(onSubmit)}>
            <FormContainer
              spacing={2}
              sx={{
                minWidth: 500,
              }}
            >
              {isExcessSubmission && (
                <>
                  <FormFieldContainer md={12}>
                    <Box className="main-title-create-quote">
                      {`Create ${capitalizeFirstLetter(
                        policyTypeName
                      )} Quote Option`}
                    </Box>
                    <Typography fontSize={'small'}>
                      <Box style={{ fontWeight: '400', fontSize: '20px' }}>
                        Primary Policy
                      </Box>
                      The primary policy terms that were bound for{' '}
                      {insuredEntityName?.replace(/\.$/, '') ?? 'the insured'}.
                    </Typography>
                  </FormFieldContainer>
                  <PrimaryInsurerSelectField />

                  <FormNumberInputAdornmentField
                    adornmentText={'$'}
                    name="insurerPrimaryLimit"
                    label={'PRIMARY POLICY LIMIT'}
                    data-testid={'Primary-policy-limit'}
                    thousandSeparator=","
                    containerWidth={12}
                    required
                    decimalScale={0}
                  />

                  <FormNumberInputAdornmentField
                    adornmentText={'$'}
                    name="insurerPrimaryRetention"
                    label={'PRIMARY POLICY RETENTION'}
                    data-testid={'Primary-policy-retention'}
                    thousandSeparator=","
                    containerWidth={12}
                    required
                  />

                  <FormNumberInputAdornmentField
                    adornmentText={'$'}
                    name="insurerPrimaryPremium"
                    label={'PRIMARY POLICY PREMIUM'}
                    thousandSeparator=","
                    containerWidth={12}
                    required
                  />
                </>
              )}

              {!isExcessSubmission && (
                <>
                  <FormNumberInputAdornmentField
                    adornmentText={'$'}
                    name="convergePrimaryAggregateLimit"
                    label={
                      isExcessSubmission
                        ? 'Converge primary aggregate limit'
                        : 'Limit'
                    }
                    thousandSeparator=","
                    containerWidth={12}
                    required
                  />

                  <FormNumberInputAdornmentField
                    adornmentText={'$'}
                    name="convergePrimaryPolicyRetention"
                    label={
                      isExcessSubmission
                        ? 'Converge primary policy retention'
                        : 'Retention'
                    }
                    thousandSeparator=","
                    containerWidth={12}
                    required
                  />
                </>
              )}
            </FormContainer>
          </form>
          <Box className="cancel-create-quote-modal-btn-container">
            <Box className="cancel-quote-modal-btn-container">
              <NewConvergeButton
                variant="outlined"
                onClick={() => {
                  onClose();
                }}
                label={'CANCEL'}
              />
            </Box>
            <NewConvergeButton
              variant="contained"
              label="Create"
              loading={loading}
              onClick={() => {
                formMethods.handleSubmit(onSubmit)();
              }}
              disabled={disabled}
            />
          </Box>
        </FormProvider>
      </Box>
    </Modal>
    // <DialogLayout
    //   showCloseButton
    //   onCloseIconClick={() => {
    //     onClose();
    //   }}
    //   onClose={() => {
    //     onClose();
    //   }}
    //   className="create-quote-option"
    //   open={isOpen}
    //   header={`Create ${policyTypeName} quote option`}
    //   id={'create-quote-option-dialog'}
    //   footer={
    //     <Stack direction="row" spacing={2}>
    //       <NewConvergeButton
    //         variant="outlined"
    //         label="Cancel"
    //         data-testid="Cancel"
    //         onClick={() => {
    //           onClose();
    //         }}
    //       />
    //       <NewConvergeButton
    //         variant="contained"
    //         label="Create"
    //         loading={loading}
    //         onClick={() => {
    //           form.handleSubmit(onSubmit)();
    //         }}
    //         disabled={disabled}
    //       />
    //     </Stack>
    //   }
    // >

    // </DialogLayout>
  );
};
